// Consolidated "apply animation/image-edit result to its source box" decision,
// previously copy-pasted at three call sites: EditorShortcuts.jsx's
// onAcceptAnimation, EditorApp.jsx's ImageAnimatorModal mount, and
// JobWidgetHost.jsx's job-approved handler.
//
// "Canlandır" (animate) çıktısı mp4/webm gibi bir video olabilir. Kaynak kutu
// bir HotspotImages (görsel) ise sadece url'i video yapmak <img src=mp4> =
// kırık zincir ikonu üretir. Bu durumda görsel kutusunu aynı konum/boyutta
// bir EnrichedPlayer (video) kutusuyla değiştirmek gerekir; aksi halde url
// yerinde güncellenir. Revert is the mirror: an image url landing on an
// EnrichedPlayer source swaps back to a HotspotImages box.
//
// Pure decision function: reads only its explicit arguments, never dispatches
// and never touches IdealStudio.Plugins — each call site keeps its own
// dispatch/createBox/onToolbarUpdated/onBoxDeleted wiring (and any
// plugin-availability gating it already had) around the decision below.
//
// Both swap directions carry the source box's authored timeline (if any) onto
// newBox.timeline — see buildSwapDecision — so a shorter/longer replacement
// clip loops/holds within the scene's already-composed range instead of
// shrinking it to the clip's own duration.
import { isSlide, isContainedView } from "../../common/utils";
import { ID_PREFIX_BOX } from "../../common/constants";
import { classifyPluginTrackType } from "../../common/media_track_types.es6";

const VIDEO_URL_RE = /\.(mp4|webm|mov|m4v)(\?|#|$)/i;

// Geometry/newBox resolution shared by both swap directions — only the
// target plugin name and any extra persisted state differ.
function buildSwapDecision({
  boxId,
  url,
  srcBox,
  srcTb,
  pluginName,
  initialState,
  containedViewSelected,
  containedViews,
  navItems,
  navItemSelected,
}) {
  const struct = srcTb.structure || {};
  const cv =
    containedViewSelected &&
    containedViews?.[containedViewSelected] &&
    isContainedView(containedViewSelected);
  const navItem = cv ? containedViews[containedViewSelected] : navItems?.[navItemSelected];
  const inASlide = navItem ? isSlide(navItem.type) : false;
  const page = cv ? containedViewSelected : navItem?.id || navItemSelected || 0;
  const newBoxId = ID_PREFIX_BOX + Date.now();

  return {
    kind: "boxSwap",
    newBoxId,
    newBox: {
      parent: srcBox.parent,
      container: srcBox.container,
      page,
      id: newBoxId,
      name: pluginName,
      position: srcBox.position,
      url,
      width: struct.width,
      height: struct.height,
      widthUnit: struct.widthUnit,
      heightUnit: struct.heightUnit,
      // Carry the source box's authored timeline range onto the swapped box —
      // the scene is composed and narration-aligned to that range, so a
      // shorter/longer replacement clip loops/holds within it rather than
      // shrinking it to the clip's own duration. Absent on legacy boxes with
      // no explicit timeline: omit the key so addBoxThunk's own default-stamp
      // path runs exactly as it does for any other newly created box (see
      // common/actions.es6 addBoxThunk / reducers/boxes_by_id.es6 boxCreator —
      // both read this from newBox.timeline via createBox's initialParams
      // pass-through, no separate follow-up action needed).
      // Strip shared-row membership when a swap changes media type.
      ...(srcBox.timeline
        ? {
            timeline:
              srcBox.timeline.trackId &&
              classifyPluginTrackType(pluginName) !==
                classifyPluginTrackType(srcTb?.pluginId || srcBox.pluginId)
                ? (({ trackId, ...rest }) => rest)(srcBox.timeline) // eslint-disable-line no-unused-vars
                : srcBox.timeline,
          }
        : {}),
      ...(initialState ? { initialState } : {}),
    },
    inASlide,
    oldBoxId: boxId,
    parent: srcBox.parent,
    container: srcBox.container,
    page,
  };
}

export function applyAnimationResult({
  boxId,
  url,
  boxes,
  pluginToolbars,
  containedViewSelected,
  containedViews,
  navItems,
  navItemSelected,
  loop, // the animation's own loop flag; unknown (undefined) → true
  videoProjectId, // Video Director project the mp4 was edited in (reopens there)
}) {
  if (!boxId || !url) {
    return { kind: "noop" };
  }

  const srcBox = boxes?.[boxId];
  const srcTb = pluginToolbars?.[boxId];
  const isVideoUrl = VIDEO_URL_RE.test(url);
  const isImageSource = srcTb?.pluginId === "HotspotImages";
  const isVideoSource = srcTb?.pluginId === "EnrichedPlayer";

  if (isVideoUrl && isImageSource && srcBox) {
    return buildSwapDecision({
      boxId,
      url,
      srcBox,
      srcTb,
      pluginName: "EnrichedPlayer",
      // Stashed on the new video box so a later revert survives save/reload
      // without depending on the server's referenceId version chain (verified
      // absent for some trainings' images). See _editor/utils/revertAnimatedBox.es6.
      initialState: {
        originalImageUrl: srcTb.state?.url,
        originalImagePluginId: srcTb.pluginId,
        // Animated-image semantics: the box replaces a still image, so it must
        // look/behave like one — chromeless, muted, autoplaying, looping —
        // not like a user-added video player (base_plugin.es6's
        // getInitialParams merges this over EnrichedPlayer's own defaults of
        // controls:true/volume:1, see EnrichedPlayer.js getInitialState).
        controls: false,
        autoPlay: true,
        volume: 0,
        loop: loop ?? true,
        ...(videoProjectId ? { videoProjectId } : {}),
      },
      containedViewSelected,
      containedViews,
      navItems,
      navItemSelected,
    });
  }

  // Revert: an image result landing on a video box swaps back to a still
  // HotspotImages box — the mirror of the branch above.
  if (!isVideoUrl && isVideoSource && srcBox) {
    return buildSwapDecision({
      boxId,
      url,
      srcBox,
      srcTb,
      pluginName: "HotspotImages",
      containedViewSelected,
      containedViews,
      navItems,
      navItemSelected,
    });
  }

  return { kind: "urlUpdate", boxId };
}
